import React, { useState, useMemo, useEffect, useRef } from 'react';
import Header from './components/Header';
import StatCard from './components/StatCard';
import ProxyInput from './components/ProxyInput';
import ConfigPanel from './components/ConfigPanel';
import ResultsTable from './components/ResultsTable';
import ErrorPanel from './components/ErrorPanel';
import ExportPanel from './components/ExportPanel';
import { runBatchCheck } from './services/checkerApi';

export default function App() {
  const [proxiesText, setProxiesText] = useState('');
  const [config, setConfig] = useState({ protocol: 'auto', batchSize: 10 });
  const [isChecking, setIsChecking] = useState(false);
  const [currentCheckList, setCurrentCheckList] = useState([]);
  const [resultsMap, setResultsMap] = useState(new Map());
  const [error, setError] = useState(null);
  const abortRef = useRef(null);

  useEffect(() => {
    return () => { if (abortRef.current) abortRef.current.abort(); };
  }, []);

  const handleStart = async () => {
    const list = proxiesText.split('\n').map((p) => p.trim()).filter(Boolean);
    if (list.length === 0) { setError('Please enter at least one proxy.'); return; }
    setError(null);
    setIsChecking(true);
    setCurrentCheckList(list);
    setResultsMap(new Map());
    const initMap = new Map();
    list.forEach((_, i) => initMap.set(i, { checking: true }));
    setResultsMap(initMap);
    const controller = new AbortController();
    abortRef.current = controller;
    await runBatchCheck(
      list, config,
      (index, result) => {
        setResultsMap((prev) => { const next = new Map(prev); next.set(index, result); return next; });
      },
      () => setIsChecking(false),
      (err) => { setError(err.message); setIsChecking(false); },
      controller.signal
    );
  };

  const handleStop = () => {
    if (abortRef.current) { abortRef.current.abort(); setIsChecking(false); }
  };

  const stats = useMemo(() => {
    let alive = 0, dead = 0;
    resultsMap.forEach((res) => {
      if (!res || res.checking) return;
      if (res.ip && !res.error) alive++; else dead++;
    });
    const total = currentCheckList.length || proxiesText.split('\n').filter((l) => l.trim()).length;
    const done = alive + dead;
    const progress = total > 0 ? Math.round((done / total) * 100) : 0;
    return { total, alive, dead, progress };
  }, [resultsMap, currentCheckList, proxiesText]);

  const displayList = currentCheckList.length > 0
    ? currentCheckList
    : proxiesText.split('\n').filter((l) => l.trim());

  const isDone = !isChecking && [...resultsMap.values()].some(r => r && !r.checking);

  return (
    <div className="min-h-screen bg-gradient-to-br from-orange-50 via-white to-pink-50 text-slate-700 font-sans selection:bg-orange-200 pb-12">
      <Header />
      <main className="max-w-7xl mx-auto px-4 py-8 flex flex-col gap-6">

        {/* Stats */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <StatCard title="Total Loaded" value={stats.total} emoji="📦" gradient="bg-white border-blue-100" textColor="text-blue-500" />
          <StatCard title="Alive Proxies" value={stats.alive} emoji="✅" gradient="bg-white border-emerald-100" textColor="text-emerald-500" />
          <StatCard title="Dead Proxies" value={stats.dead} emoji="💀" gradient="bg-white border-red-100" textColor="text-red-400" />
          <StatCard title="Progress" value={`${stats.progress}%`} emoji="🐾" gradient="bg-white border-orange-100" textColor="text-orange-400" />
        </div>

        {/* Progress bar */}
        {isChecking && (
          <div className="w-full bg-orange-100 rounded-full h-2 overflow-hidden shadow-inner">
            <div
              className="bg-gradient-to-r from-orange-400 to-pink-400 h-2 rounded-full transition-all duration-500"
              style={{ width: `${stats.progress}%` }}
            />
          </div>
        )}

        {/* Config + Proxy Input */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 items-start">
          <div className="lg:col-span-1">
            <ConfigPanel config={config} setConfig={setConfig} disabled={isChecking} />
          </div>
          <div className="lg:col-span-2 flex flex-col gap-4">
            <ProxyInput value={proxiesText} onChange={setProxiesText} disabled={isChecking} />
            <div className="flex flex-col gap-3">
              <button
                onClick={isChecking ? handleStop : handleStart}
                className={`w-full py-3.5 rounded-2xl font-extrabold shadow-lg transition-all duration-300 flex justify-center items-center gap-2 transform active:scale-[0.98] text-white text-sm tracking-wide ${
                  isChecking
                    ? 'bg-gradient-to-r from-red-400 to-rose-400 shadow-red-200 hover:shadow-red-300'
                    : 'bg-gradient-to-r from-orange-400 to-pink-400 shadow-orange-200 hover:shadow-orange-300 hover:-translate-y-0.5'
                }`}
              >
                {isChecking ? '⏹ STOP CHECKING' : '🐱 START CHECK'}
              </button>
              <ErrorPanel error={error} />
            </div>
          </div>
        </div>

        {/* Results Table */}
        <div className="w-full" style={{ minHeight: '520px' }}>
          <ResultsTable proxyList={displayList} resultsMap={resultsMap} />
        </div>

        {/* Export Panel — only shown after checking */}
        {isDone && (
          <ExportPanel proxyList={displayList} resultsMap={resultsMap} />
        )}

      </main>
    </div>
  );
}